import React, { useState } from 'react';
import {
  formatMoney,
  formatPercent,
  formatSignedPercent,
  formatLeverValue,
} from '../../features/diagnostic/format';

const ScenarioCard = ({ scenario, isActive, onSelect, isRecommended }) => (
  <button
    type="button"
    className={`dg-scenario-tab${isActive ? ' is-active' : ''}`}
    onClick={() => onSelect(scenario.id)}
    aria-pressed={isActive}
  >
    <span className="dg-scenario-tab-name">
      {scenario.name}
      {isRecommended ? <span className="dg-scenario-badge">рекомендуем</span> : null}
    </span>
    <span className="dg-scenario-tab-coverage">
      {scenario.closesTarget ? 'Закрывает цель' : `${formatPercent(scenario.targetCoverage)} цели`}
    </span>
    <span className="dg-scenario-tab-timing">{scenario.timing.label}</span>
  </button>
);

const ScenarioDetails = ({ scenario, currentRevenue }) => (
  <div className="dg-scenario-body">
    <p className="dg-scenario-desc">{scenario.description}</p>

    <div className="dg-scenario-summary">
      <div className="dg-metric">
        <span className="dg-metric-label">Выручка станет</span>
        <span className="dg-metric-value">{formatMoney(scenario.achievedRevenue)}</span>
      </div>
      <div className="dg-metric">
        <span className="dg-metric-label">Прибавка в месяц</span>
        <span className="dg-metric-value">+{formatMoney(scenario.achievedGain)}</span>
      </div>
      <div className="dg-metric">
        <span className="dg-metric-label">Срок</span>
        <span className="dg-metric-value">{scenario.timing.label}</span>
      </div>
    </div>

    {!scenario.closesTarget ? (
      <p className="dg-scenario-warning">
        Этот сценарий закрывает {formatPercent(scenario.targetCoverage)} от нужного прироста.
        Рычаги упираются в предел вашей ниши — чтобы дойти до цели полностью, нужен более
        широкий сценарий или больше времени.
      </p>
    ) : null}

    <div className="dg-scenario-levers">
      {scenario.levers
        .filter((lever) => lever.growth > 0)
        .map((lever) => (
          <div key={lever.id} className="dg-scenario-lever">
            <div className="dg-scenario-lever-head">
              <span className="dg-scenario-lever-name">{lever.plainLabel}</span>
              <span className="dg-scenario-lever-growth">
                {formatSignedPercent(lever.growthPercent, 1)}
                {lever.atCap ? <span className="dg-scenario-lever-cap">предел ниши</span> : null}
              </span>
            </div>
            <div className="dg-scenario-lever-values">
              {formatLeverValue(lever.id, lever.currentValue)}
              <span aria-hidden="true"> → </span>
              {formatLeverValue(lever.id, lever.targetValue)}
            </div>
            <p className="dg-scenario-lever-how">{lever.how}</p>
          </div>
        ))}
    </div>

    <p className="dg-note">
      Расчёт от текущей выручки {formatMoney(currentRevenue)} в месяц. Одновременная работа
      по нескольким направлениям учтена: чем больше фронтов, тем сильнее размывается внимание.
    </p>
  </div>
);

const PaidReport = ({ result, aiReport, aiState, onRetryAi, onDownload }) => {
  const recommendedId = result.recommended ? result.recommended.id : null;
  const [activeId, setActiveId] = useState(recommendedId || 'minimal');

  const activeScenario =
    result.scenarios.find((scenario) => scenario.id === activeId) || result.scenarios[0];

  if (!activeScenario) {
    return (
      <div className="dg-step">
        <p className="dg-note">
          Чтобы построить сценарии, поставьте цель выше текущей выручки.
        </p>
      </div>
    );
  }

  return (
    <div className="dg-step dg-paid">
      <div className="dg-result-head">
        <span className="dg-result-eyebrow">Полный разбор</span>
        <h2 className="dg-step-title">Три пути к вашей цели</h2>
        <p className="dg-step-lead">
          Цель: {formatMoney(result.targetRevenue)} в месяц. Ниже — что нужно сделать
          и сколько это реально даёт.
        </p>
      </div>

      <section className="dg-block">
        <div className="dg-scenario-tabs">
          {result.scenarios.map((scenario) => (
            <ScenarioCard
              key={scenario.id}
              scenario={scenario}
              isActive={scenario.id === activeScenario.id}
              isRecommended={scenario.id === recommendedId}
              onSelect={setActiveId}
            />
          ))}
        </div>

        <ScenarioDetails scenario={activeScenario} currentRevenue={result.state.revenue} />
      </section>

      <section className="dg-block dg-block--accent">
        <div className="dg-block-head">
          <h3 className="dg-block-title">Что это значит для вас</h3>
          {aiState === 'error' ? (
            <button type="button" className="dg-link-btn" onClick={onRetryAi}>
              Повторить
            </button>
          ) : null}
        </div>

        {aiState === 'loading' ? (
          <p className="dg-ai-loading">Готовим разбор по вашим цифрам…</p>
        ) : null}

        {aiReport ? (
          <div className="dg-ai-report">
            {aiReport.summary ? <p className="dg-ai-summary">{aiReport.summary}</p> : null}

            {aiReport.steps && aiReport.steps.length ? (
              <ol className="dg-ai-steps">
                {aiReport.steps.map((step, index) => (
                  <li key={index} className="dg-ai-step">
                    <span className="dg-ai-step-title">{step.title}</span>
                    <span className="dg-ai-step-text">{step.text}</span>
                  </li>
                ))}
              </ol>
            ) : null}

            {aiReport.risks && aiReport.risks.length ? (
              <div className="dg-ai-risks">
                <h4 className="dg-ai-subtitle">На что смотреть внимательно</h4>
                <ul>
                  {aiReport.risks.map((risk, index) => (
                    <li key={index}>{risk}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {aiReport.fallback ? (
              <p className="dg-note">
                Разбор собран по расчётной модели. Развёрнутый комментарий станет доступен,
                когда подключим генерацию текста.
              </p>
            ) : null}
          </div>
        ) : null}
      </section>

      <section className="dg-block">
        <h3 className="dg-block-title">Где искать клиентов в вашей нише</h3>
        <p className="dg-block-lead">
          Только каналы, которые реально работают в Казахстане для ниши «{result.niche.label}».
        </p>

        <div className="dg-channels">
          {result.channels.channels.map((channel) => (
            <div key={channel.id} className="dg-channel">
              <span className="dg-channel-name">{channel.name}</span>
              <span className="dg-channel-purpose">{channel.purpose}</span>
            </div>
          ))}
        </div>

        {result.channels.budgetNote ? (
          <p className="dg-channel-budget">{result.channels.budgetNote}</p>
        ) : null}
        <p className="dg-note">Источник: {result.channels.source}</p>
      </section>

      <div className="dg-actions">
        <button type="button" className="dg-btn dg-btn--primary" onClick={onDownload}>
          Скачать полный разбор
        </button>
      </div>
    </div>
  );
};

export default PaidReport;
